
import {
	json,
	type LoaderFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
} from "@remix-run/node"
import {
	Form,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
	useMatches,
	useSubmit,
} from "@remix-run/react"
import { withSentry } from "@sentry/remix"
import { useEffect, useRef } from "react"
import { HoneypotProvider } from "remix-utils/honeypot/react"
import { GeneralErrorBoundary } from "./error-boundary.tsx"
import { EpicProgress } from "./progress-bar.tsx"
import { SearchBar } from "./search-bar.tsx"
import { useToast } from "./toaster.tsx"
import { Button } from "./ui/button.tsx"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from "./ui/dropdown-menu.tsx"
import { Icon, href as iconsHref } from "./ui/icon.tsx"
import { EpicToaster } from "./ui/sonner.tsx"
import { ThemeSwitch, useTheme } from "../routes/resources+/theme-switch.tsx"
import tailwindStyleSheetUrl from "./styles/tailwind.css?url"
import { getUserId, logout } from "../utils/auth.server.ts"
import { ClientHintCheck, getHints } from "../utils/client-hints.tsx"
import { prisma } from "../utils/db.server.ts"
import { getEnv } from "../utils/env.server.ts"
import { honeypot } from "../utils/honeypot.server.ts"
import { combineHeaders, getDomainUrl, getUserImgSrc } from "../utils/misc.tsx"
import { useNonce } from "../utils/nonce-provider.ts"
import { type Theme, getTheme } from "../utils/theme.server.ts"
import { makeTimings, time } from "../utils/timing.server.ts"
import { getToast } from "../utils/toast.server.ts"
import { useOptionalUser, useUser } from "../utils/user.ts"
import * as gtag from "../utils/gtags.client.ts";
import Logo from "./logo.tsx";
import { useState } from "react"
import {
    CloseButton,
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from "@headlessui/react"
import {
  ArrowPathIcon,
  Bars3Icon,
  ChartPieIcon,
  CursorArrowRaysIcon,
  FingerPrintIcon,
  SquaresPlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { ChevronDownIcon, PhoneIcon, PlayCircleIcon, PlusCircleIcon, DocumentMagnifyingGlassIcon, QuestionMarkCircleIcon, FolderPlusIcon } from "@heroicons/react/20/solid"
import { ServerSwitch } from "#app/routes/resources+/server-switch.js"
import { brandName, brandUrl } from "./branding.tsx"
import { FaExternalLinkAlt } from "react-icons/fa"

const callsToAction2 = [
    { name: "Watch demo", href: "#", icon: PlayCircleIcon },
    { name: "Ask a question", href: "#", icon: PhoneIcon },
  ]
  

const basicLinks = [
    { name: "Scripts", href: "/scripts", icon: null, popover:  [
      { name: "Browse by Type", description: "Training, Utility, Organization", href: "/categories", icon: ChartPieIcon },
      { name: "Browse by Skill", description: "View a list of skills", href: "/skills", icon: DocumentMagnifyingGlassIcon },
      //{ name: "Official Jase Owns", description: "Jaseowns personal crafted scripts", href: "/users/jaseowns", icon: FolderPlusIcon },
    //   { name: "Offical Jaseowns", description: "Connect with third-party tools", href: "#", icon: SquaresPlusIcon },
      { name: "Recent Edits", description: "Updates by members of the community", href: "/scripts/recent", icon: ArrowPathIcon },
    ], popoverCta: [
      { name: "Submit script", href: "/script/new", icon: PlusCircleIcon },
      { name: "Request a script", href: "/script/request", icon: QuestionMarkCircleIcon },
    ]
      },
    { name: "Hues", href: "/hues", icon: null, popover: null },
    { name: "Items", href: "/items", icon: null, popover: null },
    { name: "Guides", href: "/guides", icon: null, popover: [
      { name: "What is UO and how do I get started?", description: "There are many options when it comes to playing UO - learn about why this old wizard game from 1997 is still one of the greatest ever made and how you can play for free!", href: "/guides/free-to-play-uo", icon: ChartPieIcon },
    ] },
    { name: "Build Templates", href: "/builds", icon: null, popover: [
      { name: "PVM", description: "Player vs Monster builds", href: "/builds/pvm", icon: ChartPieIcon },
      { name: "PVP", description: "Player vs Player builds", href: "/builds/pvp", icon: DocumentMagnifyingGlassIcon },
    //   { name: "Mage", description: "Magery builds", href: "/builds/mage", icon: DocumentMagnifyingGlassIcon },
    //   { name: "Warrior", description: "Warrior builds", href: "/builds/warrior", icon: DocumentMagnifyingGlassIcon },
    //   { name: "Bard", description: "Bard builds", href: "/builds/bard", icon: DocumentMagnifyingGlassIcon },
    //   { name: "Gathering", description: "Gathering builds", href: "/builds/gather", icon: DocumentMagnifyingGlassIcon },
    //   { name: "Crafting", description: "Crafting builds", href: "/builds/crafting", icon: DocumentMagnifyingGlassIcon },
 ], popoverCta: [
  { name: "Submit build", href: "/builds/new", icon: PlusCircleIcon },
  { name: "Request a build", href: "/builds/request", icon: QuestionMarkCircleIcon },
] },
    { name: "Servers", href: "/servers", icon: null, popover: null },
    { name: "Support", href: "/support", icon: null, popover: null },
    //{ name: "Company", href: "/company", icon: null },
]

export default function Footer(
    {user, searchBar, selectedServer}: {
        user: ReturnType<typeof useOptionalUser>
        searchBar: React.ReactNode,
        selectedServer: string | null
    }
) {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const formRef = useRef<HTMLFormElement>(null)

    return (


<footer className="bg-muted">
    <div className="mx-auto w-full max-w-screen-xl">
      <div className="grid grid-cols-2 gap-8 px-4 py-6 lg:py-8 md:grid-cols-4">
        <div>
            <h2 className="mb-6 text-sm font-semibold text-gray-900 uppercase dark:text-white">{brandUrl}</h2>
            <ul className="text-gray-500 dark:text-gray-400 font-medium">
                <li className="mb-4">
                    <Link to="/" className=" hover:underline">Home</Link>
                </li>
                <li className="mb-4">
                    <Link to="/about" className=" hover:underline">About</Link>
                </li>
                <li className="mb-4">
                    <Link to="/careers" className=" hover:underline">Careers</Link>
                </li>
                {/* <li className="mb-4">
                    <Link to="/blog" className=" hover:underline">Blog</Link>
                </li> */}
            </ul>
        </div>
        <div>
            <h2 className="mb-6 text-sm font-semibold text-gray-900 uppercase dark:text-white">Help center</h2>
            <ul className="text-gray-500 dark:text-gray-400 font-medium">
                <li className="mb-4">
                    <a href="https://discord.com/servers/jaseowns-442558623298617354" target="_blank" className="hover:underline">Discord Server <FaExternalLinkAlt className='inline' /></a>
                </li>
                {/* <li className="mb-4">
                    <a href="https://x.com/jaseowns" target="_blank" className="hover:underline">Twitter</a>
                </li>
                <li className="mb-4">
                    <a href="https://facebook.com/jaseowns" target="_blank" className="hover:underline">Facebook</a>
                </li> */}
                <li className="mb-4">
                    <Link to="/support" className="hover:underline">Support</Link>
                </li>
                {/* <li className="mb-4">
                    <Link to="/questions" className="hover:underline">Questions</Link>
                </li> */}
            </ul>
        </div>
        <div>
            <h2 className="mb-6 text-sm font-semibold text-gray-900 uppercase dark:text-white">Legal</h2>
            <ul className="text-gray-500 dark:text-gray-400 font-medium">
                <li className="mb-4">
                    <Link to="/privacy" className=" hover:underline">Privacy Policy</Link>
                </li>
                <li className="mb-4">
                    <Link to="/terms" className=" hover:underline">Terms &amp; Conditions</Link>
                </li>
                <li className="mb-4">
                    <Link to="/contact" className="hover:underline">Contact Us</Link>
                </li>
            </ul>
        </div>
        <div>
            <h2 className="mb-6 text-sm font-semibold text-gray-900 uppercase dark:text-white">Options</h2>
            <ul className="text-gray-500 dark:text-gray-400 font-medium">
            <li className="mb-4">
                    <Link to="/scripts" className=" hover:underline">Scripts</Link>
                </li>
                <li className="mb-4">
                    <Link to="/builds" className=" hover:underline">Builds</Link>
                </li>
                <li className="mb-4">
                    <Link to="/items" className=" hover:underline">Items</Link>
                </li>
            </ul>
        </div>
    </div>
    <div className="px-4 py-6 bg-gray-100 dark:bg-gray-700 md:flex md:items-center md:justify-between">
        <span className="text-sm text-gray-500 dark:text-gray-300 sm:text-center">© 2024 {brandUrl} is part of the <a href="https://jaseowns.com/" className="underline">Jase Owns LLC</a> network of websites.</span>
        <div className="flex mt-4 sm:justify-center md:mt-0 space-x-5 rtl:space-x-reverse">
            {/* <a href="#" className="text-gray-400 hover:text-gray-900 dark:hover:text-white">
                  <svg className="w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 8 19">
                        <path fillRule="evenodd" d="M6.135 3H8V0H6.135a4.147 4.147 0 0 0-4.142 4.142V6H0v3h2v9.938h3V9h2.021l.592-3H5V3.591A.6.6 0 0 1 5.592 3h.543Z" clipRule="evenodd"/>
                    </svg>
                  <span className="sr-only">Facebook page</span>
              </a> */}
              <a href="https://www.reddit.com/r/jaseowns/" className="text-gray-400 hover:text-gray-900 dark:hover:text-white">
                  <svg className="w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 50 50">
                        <path d="M 29 3 C 28.0625 3 27.164063 3.382813 26.5 4 C 25.835938 4.617188 25.363281 5.433594 25 6.40625 C 24.355469 8.140625 24.085938 10.394531 24.03125 13.03125 C 19.234375 13.179688 14.820313 14.421875 11.28125 16.46875 C 10.214844 15.46875 8.855469 14.96875 7.5 14.96875 C 6.089844 14.96875 4.675781 15.511719 3.59375 16.59375 C 1.425781 18.761719 1.425781 22.238281 3.59375 24.40625 L 3.84375 24.65625 C 3.3125 26.035156 3 27.488281 3 29 C 3 33.527344 5.566406 37.585938 9.5625 40.4375 C 13.558594 43.289063 19.007813 45 25 45 C 30.992188 45 36.441406 43.289063 40.4375 40.4375 C 44.433594 37.585938 47 33.527344 47 29 C 47 27.488281 46.6875 26.035156 46.15625 24.65625 L 46.40625 24.40625 C 48.574219 22.238281 48.574219 18.761719 46.40625 16.59375 C 45.324219 15.511719 43.910156 14.96875 42.5 14.96875 C 41.144531 14.96875 39.785156 15.46875 38.71875 16.46875 C 35.195313 14.433594 30.800781 13.191406 26.03125 13.03125 C 26.09375 10.546875 26.363281 8.46875 26.875 7.09375 C 27.164063 6.316406 27.527344 5.757813 27.875 5.4375 C 28.222656 5.117188 28.539063 5 29 5 C 29.460938 5 29.683594 5.125 30.03125 5.40625 C 30.378906 5.6875 30.785156 6.148438 31.3125 6.6875 C 32.253906 7.652344 33.695313 8.714844 36.09375 8.9375 C 36.539063 11.238281 38.574219 13 41 13 C 43.75 13 46 10.75 46 8 C 46 5.25 43.75 3 41 3 C 38.605469 3 36.574219 4.710938 36.09375 6.96875 C 34.3125 6.796875 33.527344 6.109375 32.75 5.3125 C 32.300781 4.851563 31.886719 4.3125 31.3125 3.84375 C 30.738281 3.375 29.9375 3 29 3 Z M 41 5 C 42.667969 5 44 6.332031 44 8 C 44 9.667969 42.667969 11 41 11 C 39.332031 11 38 9.667969 38 8 C 38 6.332031 39.332031 5 41 5 Z M 25 15 C 30.609375 15 35.675781 16.613281 39.28125 19.1875 C 42.886719 21.761719 45 25.226563 45 29 C 45 32.773438 42.886719 36.238281 39.28125 38.8125 C 35.675781 41.386719 30.609375 43 25 43 C 19.390625 43 14.324219 41.386719 10.71875 38.8125 C 7.113281 36.238281 5 32.773438 5 29 C 5 25.226563 7.113281 21.761719 10.71875 19.1875 C 14.324219 16.613281 19.390625 15 25 15 Z M 7.5 16.9375 C 8.203125 16.9375 8.914063 17.148438 9.53125 17.59375 C 7.527344 19.03125 5.886719 20.769531 4.75 22.71875 C 3.582031 21.296875 3.660156 19.339844 5 18 C 5.714844 17.285156 6.609375 16.9375 7.5 16.9375 Z M 42.5 16.9375 C 43.390625 16.9375 44.285156 17.285156 45 18 C 46.339844 19.339844 46.417969 21.296875 45.25 22.71875 C 44.113281 20.769531 42.472656 19.03125 40.46875 17.59375 C 41.085938 17.148438 41.796875 16.9375 42.5 16.9375 Z M 17 22 C 14.800781 22 13 23.800781 13 26 C 13 28.199219 14.800781 30 17 30 C 19.199219 30 21 28.199219 21 26 C 21 23.800781 19.199219 22 17 22 Z M 33 22 C 30.800781 22 29 23.800781 29 26 C 29 28.199219 30.800781 30 33 30 C 35.199219 30 37 28.199219 37 26 C 37 23.800781 35.199219 22 33 22 Z M 17 24 C 18.117188 24 19 24.882813 19 26 C 19 27.117188 18.117188 28 17 28 C 15.882813 28 15 27.117188 15 26 C 15 24.882813 15.882813 24 17 24 Z M 33 24 C 34.117188 24 35 24.882813 35 26 C 35 27.117188 34.117188 28 33 28 C 31.882813 28 31 27.117188 31 26 C 31 24.882813 31.882813 24 33 24 Z M 34.15625 33.84375 C 34.101563 33.851563 34.050781 33.859375 34 33.875 C 33.683594 33.9375 33.417969 34.144531 33.28125 34.4375 C 33.28125 34.4375 32.757813 35.164063 31.4375 36 C 30.117188 36.835938 28.058594 37.6875 25 37.6875 C 21.941406 37.6875 19.882813 36.835938 18.5625 36 C 17.242188 35.164063 16.71875 34.4375 16.71875 34.4375 C 16.492188 34.082031 16.066406 33.90625 15.65625 34 C 15.332031 34.082031 15.070313 34.316406 14.957031 34.632813 C 14.84375 34.945313 14.894531 35.292969 15.09375 35.5625 C 15.09375 35.5625 15.863281 36.671875 17.46875 37.6875 C 19.074219 38.703125 21.558594 39.6875 25 39.6875 C 28.441406 39.6875 30.925781 38.703125 32.53125 37.6875 C 34.136719 36.671875 34.90625 35.5625 34.90625 35.5625 C 35.207031 35.273438 35.296875 34.824219 35.128906 34.441406 C 34.960938 34.058594 34.574219 33.820313 34.15625 33.84375 Z"></path>
                    </svg>
                  <span className="sr-only">Reddit community</span>
              </a>
              <a href="https://discord.com/servers/jaseowns-442558623298617354" className="text-gray-400 hover:text-gray-900 dark:hover:text-white">
                  <svg className="w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 21 16">
                        <path d="M16.942 1.556a16.3 16.3 0 0 0-4.126-1.3 12.04 12.04 0 0 0-.529 1.1 15.175 15.175 0 0 0-4.573 0 11.585 11.585 0 0 0-.535-1.1 16.274 16.274 0 0 0-4.129 1.3A17.392 17.392 0 0 0 .182 13.218a15.785 15.785 0 0 0 4.963 2.521c.41-.564.773-1.16 1.084-1.785a10.63 10.63 0 0 1-1.706-.83c.143-.106.283-.217.418-.33a11.664 11.664 0 0 0 10.118 0c.137.113.277.224.418.33-.544.328-1.116.606-1.71.832a12.52 12.52 0 0 0 1.084 1.785 16.46 16.46 0 0 0 5.064-2.595 17.286 17.286 0 0 0-2.973-11.59ZM6.678 10.813a1.941 1.941 0 0 1-1.8-2.045 1.93 1.93 0 0 1 1.8-2.047 1.919 1.919 0 0 1 1.8 2.047 1.93 1.93 0 0 1-1.8 2.045Zm6.644 0a1.94 1.94 0 0 1-1.8-2.045 1.93 1.93 0 0 1 1.8-2.047 1.918 1.918 0 0 1 1.8 2.047 1.93 1.93 0 0 1-1.8 2.045Z"/>
                    </svg>
                  <span className="sr-only">Discord community</span>
              </a>
              <a href="https://twitter.com/jaseowns" className="text-gray-400 hover:text-gray-900 dark:hover:text-white">
                  <svg className="w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 17">
                    <path fillRule="evenodd" d="M20 1.892a8.178 8.178 0 0 1-2.355.635 4.074 4.074 0 0 0 1.8-2.235 8.344 8.344 0 0 1-2.605.98A4.13 4.13 0 0 0 13.85 0a4.068 4.068 0 0 0-4.1 4.038 4 4 0 0 0 .105.919A11.705 11.705 0 0 1 1.4.734a4.006 4.006 0 0 0 1.268 5.392 4.165 4.165 0 0 1-1.859-.5v.05A4.057 4.057 0 0 0 4.1 9.635a4.19 4.19 0 0 1-1.856.07 4.108 4.108 0 0 0 3.831 2.807A8.36 8.36 0 0 1 0 14.184 11.732 11.732 0 0 0 6.291 16 11.502 11.502 0 0 0 17.964 4.5c0-.177 0-.35-.012-.523A8.143 8.143 0 0 0 20 1.892Z" clipRule="evenodd"/>
                </svg>
                  <span className="sr-only">Twitter page</span>
              </a>
              <a href="https://github.com/jaseowns" className="text-gray-400 hover:text-gray-900 dark:hover:text-white">
                  <svg className="w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 .333A9.911 9.911 0 0 0 6.866 19.65c.5.092.678-.215.678-.477 0-.237-.01-1.017-.014-1.845-2.757.6-3.338-1.169-3.338-1.169a2.627 2.627 0 0 0-1.1-1.451c-.9-.615.07-.6.07-.6a2.084 2.084 0 0 1 1.518 1.021 2.11 2.11 0 0 0 2.884.823c.044-.503.268-.973.63-1.325-2.2-.25-4.516-1.1-4.516-4.9A3.832 3.832 0 0 1 4.7 7.068a3.56 3.56 0 0 1 .095-2.623s.832-.266 2.726 1.016a9.409 9.409 0 0 1 4.962 0c1.89-1.282 2.717-1.016 2.717-1.016.366.83.402 1.768.1 2.623a3.827 3.827 0 0 1 1.02 2.659c0 3.807-2.319 4.644-4.525 4.889a2.366 2.366 0 0 1 .673 1.834c0 1.326-.012 2.394-.012 2.72 0 .263.18.572.681.475A9.911 9.911 0 0 0 10 .333Z" clipRule="evenodd"/>
                  </svg>
                  <span className="sr-only">GitHub account</span>
              </a>
        </div>
      </div>
    </div>
</footer>

    )
}


